import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import "./ImageDialog.css";

const DEFAULT_ASPECT_RATIO = 16 / 9;
const resolveCanvasSize = () => ({ width: 1280, height: 720 });

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const CoverImageDialog = ({ isOpen, initialFile, onClose, onComplete }) => {
  const cropperRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setImageSrc(null);
      setIsProcessing(false);
      return;
    }

    if (initialFile) {
      fileToDataUrl(initialFile).then(setImageSrc).catch(() => setImageSrc(null));
    } else {
      setImageSrc(null);
    }
  }, [isOpen, initialFile]);

  useEffect(() => {
    if (imageSrc && cropperRef.current) {
      cropperRef.current.cropper.setAspectRatio(DEFAULT_ASPECT_RATIO);
    }
  }, [imageSrc]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      fileToDataUrl(file).then(setImageSrc);
    }
  };

  const triggerFileInput = () => {
    if (typeof document === "undefined") return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = handleFileSelect;
    input.click();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (file && file.type.startsWith("image/")) {
      fileToDataUrl(file).then(setImageSrc);
    }
  };

  const handleDragOver = (event) => event.preventDefault();

  const handleCancel = () => {
    setImageSrc(null);
    onClose();
  };

  const handleInsert = async () => {
    if (!cropperRef.current) return;
    const cropper = cropperRef.current.cropper;

    if (!imageSrc) {
      triggerFileInput();
      return;
    }

    setIsProcessing(true);
    try {
      const size = resolveCanvasSize();
      const canvas = cropper.getCroppedCanvas({
        width: size.width,
        height: size.height,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high",
      });

      const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((result) => {
          if (result) {
            resolve(result);
          } else {
            reject(new Error("無法轉換裁切結果"));
          }
        }, "image/jpeg", 0.9);
      });

      const fileName = `cover-${Date.now()}.jpg`;
      const file = new File([blob], fileName, { type: "image/jpeg" });

      onComplete({ file, dataUrl, aspectRatio: DEFAULT_ASPECT_RATIO });
      setImageSrc(null);
      onClose();
    } catch (error) {
      console.error("封面裁切失敗", error);
      alert("封面裁切失敗，請重試");
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const dialog = (
    <div className="image-dialog-overlay" onClick={(e) => e.target === e.currentTarget && handleCancel()}>
      <div className="image-dialog cover-image-dialog">
        <div className="image-dialog-header">
          <h3>裁切封面圖片</h3>
          <button className="image-dialog-close" onClick={handleCancel} aria-label="關閉裁切器">
            ×
          </button>
        </div>

        <div className="image-dialog-content">
          {!imageSrc ? (
            <div
              className="image-upload-area"
              onClick={triggerFileInput}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <div className="upload-icon">📷</div>
              <div className="upload-text">選擇或拖放封面圖片</div>
              <div className="upload-hint">建議尺寸 1280×720，支援 JPG、PNG、WebP</div>
            </div>
          ) : (
            <div className="image-cropper-container">
              <div className="cropper-wrapper">
                <Cropper
                  src={imageSrc}
                  style={{ height: "100%", width: "100%" }}
                  initialAspectRatio={DEFAULT_ASPECT_RATIO}
                  guides
                  viewMode={1}
                  background={false}
                  responsive
                  autoCropArea={0.9}
                  checkOrientation={false}
                  ref={cropperRef}
                />
              </div>
              <div className="cropper-fixed-hint">封面比例已固定為 16:9</div>
            </div>
          )}
        </div>

        <div className="image-dialog-actions">
          <button
            type="button"
            className="image-btn secondary"
            onClick={handleCancel}
            disabled={isProcessing}
          >
            取消
          </button>
          <button
            type="button"
            className="image-btn primary"
            onClick={handleInsert}
            disabled={isProcessing || !imageSrc}
          >
            {isProcessing ? "處理中..." : "套用封面"}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") {
    return dialog;
  }

  return createPortal(dialog, document.body);
};

export default CoverImageDialog;
